import type { SmartSort } from '@iheartjane/dm-sdk';
import type { PropsWithChildren } from 'react';
import { createContext, useMemo } from 'react';
import { useParams } from 'react-router';

import { useFetchProduct, useSimilarProductsRow } from '@jane/dm/internal';
import type { AlgoliaProduct } from '@jane/search/types';
import { useEnsureProductId } from '@jane/shared-ecomm/hooks';
import type { MaterializedMenuProduct } from '@jane/shared/types';

import { ProductDetailSkeleton } from '../../../components';
import type { RootLoaderType } from '../../../data-access/lib/loaders/rootLoader';
import type { InternalFeeling } from '../../../data-access/lib/queries/reviews';
import { useProductFeelings } from '../../../data-access/lib/queries/reviews';
import { useTypedRouteLoaderData } from '../../../hooks/lib/useTypedRouteLoaderData';
import { useEnsureContextWithinProvider } from '../hooks/useEnsureContextWithinProvider';

interface ProductDetailsContext {
  feelings: InternalFeeling[];
  isFeelingsLoading: boolean;
  isPdpRowLoading: boolean;
  pdpRowInstance: SmartSort<AlgoliaProduct> | undefined;
  product: MaterializedMenuProduct;
  productId: number;
  storeId: number;
}

const ProductDetailsContext = createContext<ProductDetailsContext | null>(null);

export const ProductDetailsProvider = ({ children }: PropsWithChildren) => {
  const { storeId } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { productId: productIdParam } = useParams<{ productId: string }>();

  const productId = useEnsureProductId(productIdParam);

  const {
    product: smartSortProduct,
    isLoading: isProductLoading,
    isSuccess: isProductSuccess,
  } = useFetchProduct<MaterializedMenuProduct>({
    productId,
    storeId,
  });

  const product = smartSortProduct?.attributes;

  if (isProductSuccess && !product) {
    throw new Error(
      '[ProductDetailsProvider]: No product returned — it is likely out of stock or hidden by the store.'
    );
  }

  const { data: feelings = [], isLoading: isFeelingsLoading } =
    useProductFeelings(productId);

  const { instance: pdpRowInstance, isLoading: isPdpRowLoading } =
    useSimilarProductsRow({
      productId,
      searchAttributes: ['*'],
      storeId,
    });

  const contextValue = useMemo<ProductDetailsContext | null>(
    () =>
      product
        ? {
            feelings,
            isFeelingsLoading,
            isPdpRowLoading,
            pdpRowInstance,
            product,
            productId,
            storeId,
          }
        : null,
    [
      feelings,
      isFeelingsLoading,
      isPdpRowLoading,
      pdpRowInstance,
      product,
      productId,
      storeId,
    ]
  );

  if (isProductLoading || !contextValue) {
    return <ProductDetailSkeleton />;
  }

  return (
    <ProductDetailsContext.Provider value={contextValue}>
      {children}
    </ProductDetailsContext.Provider>
  );
};

export const useProductDetails = () =>
  useEnsureContextWithinProvider(ProductDetailsContext, 'ProductDetails');
